import { useCallback, useMemo, useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';

import debounce from 'bloko/common/debounce';

import getAreaMetro from 'src/components/Search/Common/Metro/api/getAreaMetro';
import { ModelDataMetro } from 'src/components/Search/Common/Metro/types';
import { filterMetroItems } from 'src/components/Search/Resume/Filters/Geo/Filters/Metro/utils/metroUtils';
import { useSelector } from 'src/hooks/useSelector';
import { appendMetroCacheItem } from 'src/models/search/common/geoDrawer';

import { useGeoFiltersAnalytics } from 'src/components/Search/Resume/Filters/Geo/hooks/useAnalytics';

const FILTER_DEBOUNCE = 300;
const SEARCH_QUERY_LIMIT_LOW = 2;

export const useSearchMetroState = (selectedAreaId: number) => {
    const [isAnalyticsSent, setAnalyticsSent] = useState(false);
    const { sendEventSearchMetro } = useGeoFiltersAnalytics();

    const dispatch = useDispatch();
    const metroCache = useSelector((state) => state.searchGeoDrawer.metroCache[selectedAreaId]);
    const [searchQuery, setSearchQuery] = useState('');
    const [searchItems, setSearchItems] = useState<Array<ModelDataMetro>>(filterMetroItems(searchQuery, metroCache));
    const hasCache = !!metroCache;

    const debouncedSearch = useMemo(
        () =>
            debounce((query: string, metroCache: ModelDataMetro[]) => {
                if (query.length >= SEARCH_QUERY_LIMIT_LOW) {
                    const searchedItems = filterMetroItems(query, metroCache);
                    setSearchItems(searchedItems);

                    if (!isAnalyticsSent) {
                        setAnalyticsSent(true);
                        sendEventSearchMetro();
                    }
                } else if (!query) {
                    setSearchItems(metroCache);

                    if (isAnalyticsSent) {
                        setAnalyticsSent(false);
                    }
                }
            }, FILTER_DEBOUNCE),
        [isAnalyticsSent, sendEventSearchMetro]
    );

    const handleQueryChange = useCallback(
        (query: string) => {
            setSearchQuery(query);
            debouncedSearch(query, metroCache);
        },
        [debouncedSearch, metroCache]
    );

    useEffect(() => {
        if (metroCache) {
            setSearchItems(metroCache);
        }
    }, [metroCache]);

    useEffect(() => {
        if (!hasCache) {
            const abortController = new AbortController();

            void getAreaMetro(String(selectedAreaId), abortController.signal)
                .then((data) => {
                    dispatch([appendMetroCacheItem({ [selectedAreaId]: data.result })]);
                })
                .catch((e) => {
                    console.error(e);
                });

            return () => {
                abortController.abort();
            };
        }

        return undefined;
    }, [hasCache, selectedAreaId, dispatch]);

    return useMemo(
        () => ({
            query: searchQuery,
            searchItems,
            allItems: metroCache,
            onQueryChange: handleQueryChange,
        }),
        [searchQuery, searchItems, metroCache, handleQueryChange]
    );
};
